import { useState } from 'react'
import { useCampaignV2Store } from '@forwardflow/features/campaign-v2/store/editor.store'
import {
   addLeadType,
   describePool,
   poolName,
   CONTENT_SECTIONS,
   ContentSection,
} from './model'
export const LeadType = ({
   poolId,
   onAdded,
   onContent,
   onChooseCategory,
}: {
   poolId?: string
   onAdded: (id: string) => void
   onContent?: (section: ContentSection) => void
   onChooseCategory?: () => void
}) => {
   const { config, setConfig, categoryLabels } = useCampaignV2Store()
   const pool = config?.contentPools.find((p) => p.id === poolId)
   const subtype = config?.subtypes.find((s) => s.poolId === poolId)
   const [category, setCategory] = useState(
      pool?.businessTypeId || config?.businessTypeIds[0] || 0
   )
   const [newName, setNewName] = useState('')
   const [newDescription, setNewDescription] = useState('')
   const [copy, setCopy] = useState('')
   if (!config) return null
   if (!pool && !config.businessTypeIds.length)
      return (
         <div className="cw-surface">
            <h2>Choose a business type first</h2>
            <p className="cw-inline-note">
               A subtype belongs to your campaign’s business type, such as a
               first-time buyer within Buyer.
            </p>
            <button
               type="button"
               className="cw-button primary"
               onClick={onChooseCategory}
            >
               Choose business type
            </button>
         </div>
      )
   const name = pool ? poolName(config, pool, categoryLabels) : newName
   const description = pool ? subtype?.description || '' : newDescription
   const update = (nextName: string, nextDescription: string) =>
      pool
         ? setConfig((c) => describePool(c, pool.id, nextName, nextDescription))
         : (setNewName(nextName), setNewDescription(nextDescription))
   return (
      <div data-testid="lead-type-description">
         <div className="cw-title">
            <div>
               <h2>{pool ? name : 'Create a subtype'}</h2>
               <p>
                  {pool
                     ? 'Describe which leads should receive this content.'
                     : 'Give it a name and describe which leads belong here. Then choose its content. All subtypes share the campaign cadence.'}
               </p>
            </div>
            {pool?.isDefault && <span className="cw-status">General</span>}
         </div>
         <div className="cw-surface space-y-6">
            {!pool && (
               <div className="cw-field">
                  Business type
                  <div
                     className="flex flex-wrap gap-2"
                     role="radiogroup"
                     aria-label="Subtype business type"
                  >
                     {config.businessTypeIds.map((id) => (
                        <button
                           type="button"
                           role="radio"
                           aria-checked={category === id}
                           key={id}
                           className={`cw-button ${category === id ? 'primary' : ''}`}
                           onClick={() => {
                              setCategory(id)
                              setCopy('')
                           }}
                           data-testid={`subtype-category-${id}`}
                        >
                           {categoryLabels[id] || `Category ${id}`}
                        </button>
                     ))}
                  </div>
                  <span className="cw-muted">
                     {categoryLabels[category]} content will be shown for this
                     subtype.
                  </span>
               </div>
            )}
            <label className="cw-field">
               Name
               <input
                  value={name}
                  placeholder="For example, First-time buyer"
                  maxLength={120}
                  onChange={(e) => update(e.target.value, description)}
                  data-testid="lead-type-name"
               />
            </label>
            <label className="cw-field">
               Which leads belong here?
               <textarea
                  value={description}
                  rows={6}
                  maxLength={4000}
                  placeholder="Describe who belongs here, what they need, and when these scripts should be used."
                  onChange={(e) => update(name, e.target.value)}
                  data-testid="lead-type-description-input"
               />
               <span className="cw-muted">
                  Include the details that distinguish this type from your other
                  leads.
               </span>
            </label>
            {!!subtype?.conditions.length && (
               <p className="cw-inline-note">
                  This existing type also has matching rules. Editing its
                  description preserves those rules.
               </p>
            )}
            {!pool && (
               <>
                  <label className="cw-field">
                     Start with content from
                     <select
                        value={copy}
                        onChange={(e) => setCopy(e.target.value)}
                     >
                        <option value="">Choose content later</option>
                        {config.contentPools
                           .filter((p) => p.businessTypeId === category)
                           .map((p) => (
                              <option key={p.id} value={p.id}>
                                 {poolName(config, p, categoryLabels)}
                              </option>
                           ))}
                     </select>
                     <span className="cw-muted">
                        Copies the selections. You can change them
                        independently.
                     </span>
                  </label>
                  <button
                     type="button"
                     className="cw-button primary"
                     disabled={
                        !newName.trim() || !newDescription.trim() || !category
                     }
                     onClick={() => {
                        const next = addLeadType(
                           config,
                           category,
                           newName.trim(),
                           newDescription.trim(),
                           copy
                        )
                        setConfig(() => next.config)
                        onAdded(next.poolId)
                     }}
                     data-testid="add-lead-type"
                  >
                     Create subtype
                  </button>
               </>
            )}
         </div>
         {pool && onContent && (
            <div className="cw-surface" data-testid="subtype-content-choices">
               <h3>Choose content for {name}</h3>
               <p className="cw-inline-note">
                  Use existing templates, customize a copy, or create new
                  content.
               </p>
               {CONTENT_SECTIONS.filter((s) => s.id !== 'description').map(
                  (item) => (
                     <button
                        key={item.id}
                        type="button"
                        className="cw-nav-row"
                        onClick={() => onContent(item.id)}
                        data-testid={`open-content-${item.id}`}
                     >
                        <span className="flex-1">{item.label}</span>
                        <span className="cw-muted">
                           {pool[
                              item.id as Exclude<ContentSection, 'description'>
                           ]?.length
                              ? 'View or edit'
                              : 'Choose or create'}
                        </span>
                     </button>
                  )
               )}
            </div>
         )}
      </div>
   )
}
